import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const MAX_POLLS = 10;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | timeout | error

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let polls = 0;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      if (polls >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      polls += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState("success");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch {
        /* keep polling */
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [refresh]);

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6">
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-10 max-w-md w-full text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-[#0A2E26] animate-spin mx-auto" />
            <h1 className="font-display font-bold text-2xl text-[#0A2E26] mt-5">Confirming your investment…</h1>
            <p className="text-[#5C5C5C] mt-2">Please wait while we verify your payment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-[#059669] mx-auto" />
            <h1 className="font-display font-bold text-2xl text-[#0A2E26] mt-5">You're in!</h1>
            <p className="text-[#5C5C5C] mt-2">Your shares have been added to your portfolio.</p>
            <Button data-testid="go-dashboard-btn" onClick={() => navigate("/dashboard")}
              className="mt-6 bg-[#0A2E26] hover:bg-[#07201A] text-white rounded-md h-11 px-7">
              Go to dashboard
            </Button>
          </>
        )}
        {(state === "timeout" || state === "error") && (
          <>
            <XCircle className="w-14 h-14 text-[#D97706] mx-auto" />
            <h1 className="font-display font-bold text-2xl text-[#0A2E26] mt-5">
              {state === "timeout" ? "Still processing" : "Something went wrong"}
            </h1>
            <p className="text-[#5C5C5C] mt-2">
              {state === "timeout"
                ? "Your payment is taking longer than expected. Your dashboard will update once it clears."
                : "We couldn't confirm this payment."}
            </p>
            <Button onClick={() => navigate("/dashboard")}
              className="mt-6 bg-[#0A2E26] hover:bg-[#07201A] text-white rounded-md h-11 px-7">
              Back to dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
